import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    useTheme,
    Alert,
    Card,
    CardHeader,
    IconButton,
    Tooltip,
    Chip,
    Stack,
    Button,
    useMediaQuery,
    TablePagination
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import api from '../../services/api';

const Checkins = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkIns, setCheckIns] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const lastFetchTime = useRef(null);
    const FETCH_COOLDOWN = 30000; // 30 seconds

    // Date filter states
    const [startDate, setStartDate] = useState(() => {
        return DateTime.now().startOf('day');
    });
    const [endDate, setEndDate] = useState(() => {
        return DateTime.now().endOf('day');
    });

    const fetchCheckIns = useCallback(async (force = false) => {
        // Check if we're within the cooldown period and not forcing refresh
        if (!force && lastFetchTime.current && Date.now() - lastFetchTime.current < FETCH_COOLDOWN) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const formattedStartDate = startDate.toFormat('yyyy-MM-dd');
            const formattedEndDate = endDate.toFormat('yyyy-MM-dd');
            
            const response = await api.get(`/admin/checkins/startDate=${formattedStartDate}/endDate=${formattedEndDate}`);
            setCheckIns(response.data.data || []);
            lastFetchTime.current = Date.now();
        } catch (error) {
            setError('Failed to load check-ins');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchCheckIns();
        // Only set up auto-refresh if viewing today's data
        if (startDate.hasSame(DateTime.now(), 'day') && endDate.hasSame(DateTime.now(), 'day')) {
            const interval = setInterval(() => fetchCheckIns(true), 5 * 60 * 1000);
            return () => clearInterval(interval);
        }
    }, [startDate, endDate, fetchCheckIns]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatTime = (dateString) => {
        return format(new Date(dateString), 'h:mm a');
    };

    // Get current page of check-ins
    const displayedCheckIns = checkIns.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Check-ins
            </Typography>

            <Card sx={{ mb: 4 }}>
                <CardHeader
                    title="Check-in Records"
                    action={
                        <Tooltip title="Refresh check-ins">
                            <IconButton onClick={() => fetchCheckIns(true)} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    }
                />

                {/* Date Filters */}
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Stack 
                        direction={isMobile ? "column" : "row"} 
                        spacing={2} 
                        sx={{ mb: 4, mt: 2, px: 2 }}
                        alignItems={isMobile ? "stretch" : "center"}
                    >
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            sx={{ width: isMobile ? '100%' : 220 }}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={setEndDate}
                            minDate={startDate}
                            maxDate={DateTime.now()}
                            sx={{ width: isMobile ? '100%' : 220 }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => fetchCheckIns(true)}
                            startIcon={<SearchIcon />}
                            sx={{ height: isMobile ? '40px' : '56px' }}
                        >
                            Search
                        </Button>
                    </Stack>
                </LocalizationProvider>

                {error ? (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>
                ) : loading && checkIns.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Member Name</TableCell>
                                        <TableCell>Client ID</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedCheckIns.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                No check-ins found for the selected date range
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayedCheckIns.map((checkIn, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{formatTime(checkIn.CheckInDateTime)}</TableCell>
                                                <TableCell>{checkIn.ClientName}</TableCell>
                                                <TableCell>{checkIn.ClientID}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={checkIn.IsClientActive ? 'Active' : 'Inactive'}
                                                        color={checkIn.IsClientActive ? 'success' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={checkIns.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </>
                )}
            </Card>
        </Box>
    );
};

export default Checkins; 